import React, { useState, useCallback, useRef } from 'react';
import FileUpload from './FileUpload';
import ErrorBoundary from './ErrorBoundary';
import SEO from './SEO';
import { PDFConverter as PDFService } from '../services/pdfConverter';
import { getPDFConverterSchema, getBreadcrumbSchema } from '../utils/structuredData';
import analytics from '../services/analytics';
import '../App.css';

// Default conversion options
const DEFAULT_CONVERSION_OPTIONS = {
  orientation: 'portrait',
  format: 'a4',
  fontSize: 12,
  lineHeight: 7,
  compression: 80
};

// Utility functions
const getFileName = (file) => file.name || file.file?.name || 'Unknown file';
const generateTimestamp = () => new Date().toISOString().slice(0, 19).replace(/[:.]/g, '-');

function PDFConverter() {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isConverting, setIsConverting] = useState(false);
  const [conversionProgress, setConversionProgress] = useState(0);
  const [currentFile, setCurrentFile] = useState('');
  const [conversionResults, setConversionResults] = useState([]);
  const [mergeFiles, setMergeFiles] = useState(true); // Default to merge
  const [compressionLevel, setCompressionLevel] = useState(80); // Default compression 80%
  const [isClearing, setIsClearing] = useState(false);
  const [clearTrigger, setClearTrigger] = useState(0); // Industry standard: counter for triggering clears
  const fileUploadRef = useRef(null); // Industry standard: ref for imperative control

  const handleFilesSelected = useCallback((files) => {
    setSelectedFiles(files);
    setConversionResults([]);
  }, []);

  const handleClearResults = useCallback(async () => {
    // Industry-standard: Clear only conversion results, keep uploaded files
    
    setIsClearing(true);
    
    try {
      // Small delay to show clearing state
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // 1. Memory cleanup - revoke any object URLs that might be stored
      conversionResults.forEach(result => {
        if (result.pdf && typeof result.pdf.output === 'function') {
          try {
            const blob = result.pdf.output('blob');
            if (blob instanceof Blob) {
              const url = URL.createObjectURL(blob);
              URL.revokeObjectURL(url);
            }
          } catch (error) {
            // Ignore cleanup errors - objects might already be cleaned
            console.debug('Memory cleanup warning:', error.message);
          }
        }
      });

      // 2. Clean up using utility method
      PDFService.cleanupMultipleResources(conversionResults);

      // 3. Clear only conversion-related state (KEEP uploaded files)
      setConversionResults([]);
      setCurrentFile('');
      setConversionProgress(0);
      setIsConverting(false);
      // NOTE: Keep selectedFiles, mergeFiles checkbox, and conversion controls

      // 4. Force garbage collection hint (modern browsers)
      if (window.gc) {
        window.gc();
      }

      // 5. Clear any cached PDF data in localStorage if any exists
      try {
        localStorage.removeItem('pdf-converter-cache');
        sessionStorage.removeItem('pdf-converter-temp');
      } catch (error) {
        // Ignore storage cleanup errors
        console.debug('Storage cleanup warning:', error.message);
      }

      console.log('✅ Conversion results cleared - uploaded files preserved');
      
      // Brief success message
      setCurrentFile('Results cleared! Ready for new conversion.');
      setTimeout(() => setCurrentFile(''), 2000);
      
    } finally {
      setIsClearing(false);
    }
  }, [conversionResults]);

  const handleClearAllFiles = useCallback(async () => {
    // Industry-standard: Complete reset including uploaded files
    
    setIsClearing(true);
    
    try {
      // Small delay to show clearing state
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // 1. Memory cleanup first
      conversionResults.forEach(result => {
        if (result.pdf && typeof result.pdf.output === 'function') {
          try {
            const blob = result.pdf.output('blob');
            if (blob instanceof Blob) {
              const url = URL.createObjectURL(blob);
              URL.revokeObjectURL(url);
            }
          } catch (error) {
            console.debug('Memory cleanup warning:', error.message);
          }
        }
      });

      // 2. Clean up using utility method
      PDFService.cleanupMultipleResources(conversionResults);

      // 3. Clear ALL application state (including uploaded files)
      setSelectedFiles([]);
      setConversionResults([]);
      setCurrentFile('');
      setConversionProgress(0);
      setMergeFiles(true);
      setIsConverting(false);

      // 4. Force garbage collection hint
      if (window.gc) {
        window.gc();
      }

      // 5. Clear cached data
      try {
        localStorage.removeItem('pdf-converter-cache');
        sessionStorage.removeItem('pdf-converter-temp');
      } catch (error) {
        console.debug('Storage cleanup warning:', error.message);
      }

      // 6. Reset file input elements
      const fileInputs = document.querySelectorAll('input[type="file"]');
      fileInputs.forEach(input => {
        input.value = '';
      });

      console.log('✅ Complete reset - all files and data cleared');
      
      // Brief success message
      setCurrentFile('All files cleared successfully!');
      setTimeout(() => setCurrentFile(''), 2000);
      
      // Clear child component files
      if (fileUploadRef.current) {
        fileUploadRef.current.clearFiles();
        
        const childState = fileUploadRef.current.validateState();
        if (!childState.isValid || childState.hasFiles) {
          console.warn('⚠️ State validation failed after clear - forcing cleanup');
          fileUploadRef.current.clearFiles();
        }
      }
      
      setClearTrigger(prev => prev + 1);
      
    } finally {
      setIsClearing(false);
    }
  }, [conversionResults]);

  const handleConvertAll = async () => {
    if (selectedFiles.length === 0) {
      alert('Please upload files to convert');
      return;
    }

    const filesToConvert = selectedFiles.filter(file => file.selected);
    
    if (filesToConvert.length === 0) {
      alert('Please select files to convert by checking the boxes');
      return;
    }

    setIsConverting(true);
    setConversionProgress(0);
    setConversionResults([]);

    const conversionOptions = {
      ...DEFAULT_CONVERSION_OPTIONS,
      compression: compressionLevel
    };

    try {
      if (mergeFiles && filesToConvert.length > 1) {
        // Merge all files into single PDF
        const result = await PDFService.convertAndMergeFiles(
          filesToConvert,
          conversionOptions,
          (current, total, file, stage) => {
            if (stage === 'converting') {
              setConversionProgress((current / total) * 80); // 80% for conversion
              if (file) {
                setCurrentFile(`Converting: ${getFileName(file)}`);
              }
            } else if (stage === 'merging') {
              setConversionProgress(90); // 90% for merging
              setCurrentFile('Merging files into single PDF...');
            }
          }
        );

        setConversionProgress(100);
        
        if (result.success) {
          setCurrentFile('Merge completed! Downloading...');
          setConversionResults([{
            success: true,
            merged: true,
            pdf: result.pdf,
            processedFiles: result.processedFiles,
            totalFiles: result.totalFiles,
            errors: result.errors
          }]);

          // Track merged PDF conversion
          analytics.trackPDFConversion('merged', filesToConvert.reduce((sum, file) => sum + file.size, 0));

          // Auto-download merged PDF
          const timestamp = generateTimestamp();
          PDFService.downloadPDF(result.pdf, `merged-pdf-${timestamp}.pdf`);
          analytics.trackDownload('pdf');
        } else {
          setCurrentFile('Merge failed');
          setConversionResults([{
            success: false,
            merged: true,
            errors: result.errors,
            message: result.message
          }]);
        }
      } else {
        // Convert files individually
        const results = await PDFService.convertMultipleFiles(
          filesToConvert,
          conversionOptions,
          (current, total, file) => {
            setConversionProgress((current / total) * 100);
            if (file) {
              setCurrentFile(getFileName(file));
            }
          }
        );

        setConversionResults(results);
        setCurrentFile('Conversion completed!');
        
        // Track individual conversions
        results.forEach((result) => {
          if (result.success && result.file) {
            analytics.trackPDFConversion(result.file.type, result.file.size);
          }
        });
        
        // Auto-download successful conversions
        results.forEach((result, index) => {
          if (result.success) {
            const originalName = getFileName(result.file) || `file_${index}`;
            const pdfName = originalName.split('.')[0] + '.pdf';
            setTimeout(() => {
              PDFService.downloadPDF(result.pdf, pdfName);
              analytics.trackDownload('pdf');
            }, index * 500); // Stagger downloads
          }
        });
      }

    } catch (error) {
      console.error('Conversion error:', error);
      analytics.trackError('conversion_error', error.message);
      alert('An error occurred during conversion: ' + error.message);
    } finally {
      setIsConverting(false);
    }
  };

  const handleDownloadSingle = (result) => {
    if (result.success && result.pdf) {
      const originalName = getFileName(result.file) || 'converted';
      const pdfName = originalName.split('.')[0] + '.pdf';
      PDFService.downloadPDF(result.pdf, pdfName);
      analytics.trackDownload('pdf');
    }
  };

  const handleRetryFailed = async () => {
    const failedFiles = conversionResults.filter(result => !result.success);
    if (failedFiles.length === 0) return;

    setIsConverting(true);
    setConversionProgress(0);

    try {
      const retryResults = await PDFService.convertMultipleFiles(
        failedFiles.map(result => result.file),
        {
          ...DEFAULT_CONVERSION_OPTIONS,
          compression: compressionLevel
        },
        (current, total, file) => {
          setConversionProgress((current / total) * 100);
          if (file) {
            setCurrentFile(`Retrying: ${getFileName(file)}`);
          }
        }
      );

      // Update results
      const updatedResults = [...conversionResults];
      let retryIndex = 0;
      updatedResults.forEach((result, index) => {
        if (!result.success) {
          updatedResults[index] = retryResults[retryIndex];
          retryIndex++;
        }
      });

      setConversionResults(updatedResults);
      setCurrentFile('Retry completed!');

      // Auto-download newly successful conversions
      retryResults.forEach((result, index) => {
        if (result.success) {
          const originalName = getFileName(result.file) || `retry_${index}`;
          const pdfName = originalName.split('.')[0] + '.pdf';
          setTimeout(() => {
            PDFService.downloadPDF(result.pdf, pdfName);
          }, index * 500);
        }
      });

    } catch (error) {
      console.error('Retry error:', error);
      alert('An error occurred during retry: ' + error.message);
    } finally {
      setIsConverting(false);
    }
  };

  const successCount = conversionResults.filter(r => r.success).length;
  const failedCount = conversionResults.filter(r => !r.success).length;

  // Breadcrumb data for PDF converter
  const breadcrumbData = [
    { name: 'Home', url: '/' },
    { name: 'PDF Converter', url: '/#converter' }
  ];

  // Combined structured data for PDF converter
  const combinedStructuredData = [
    getPDFConverterSchema(),
    getBreadcrumbSchema(breadcrumbData)
  ];

  return (
    <div className="App">
      <SEO
        title="Free PDF Converter - Convert Any File to PDF Online"
        description="Convert images, documents, spreadsheets, and text files to PDF instantly. Supports batch processing, file merging, and compression. No file upload required - works entirely in your browser."
        keywords="PDF converter, convert to PDF, image to PDF, document to PDF, free PDF converter, online PDF converter, batch PDF conversion, merge PDF, compress PDF, JPG to PDF, PNG to PDF, DOCX to PDF, Excel to PDF"
        url="/#converter"
        type="article"
        canonical={`${window.location.origin}/#converter`}
        structuredData={combinedStructuredData}
      />
      <div className="container">
        <div className="pdf-header">
          <button 
            className="btn back-btn"
            onClick={() => window.location.hash = 'home'}
            style={{
              position: 'absolute',
              top: '20px',
              left: '20px',
              background: 'rgba(255, 255, 255, 0.2)',
              color: 'white',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              padding: '8px 16px',
              fontSize: '14px'
            }}
          >
            ← Back to Home
          </button>
          <h1>📄 PDF Converter</h1>
          <p>Convert your images, documents, and text files to PDF format</p>
        </div>

        <div className="card">
          <ErrorBoundary onReset={handleClearAllFiles}>
            <FileUpload 
              ref={fileUploadRef}
              onFilesSelected={handleFilesSelected} 
              onClearAll={handleClearAllFiles}
              shouldClear={clearTrigger > 0}
            />
          </ErrorBoundary>
          
          {selectedFiles.length > 0 && (() => {
            const selectedCount = selectedFiles.filter(file => file.selected).length;
            return (
            <div className="conversion-controls">
              {selectedFiles.length > 1 && (
                <div className="merge-option">
                  <label className="merge-checkbox">
                    <input
                      type="checkbox"
                      checked={mergeFiles}
                      onChange={(e) => setMergeFiles(e.target.checked)}
                    />
                    <span className="checkmark"></span>
                    Merge selected files into one PDF
                  </label>
                  <p className="merge-description">
                    {selectedCount === 0 
                      ? 'Please select files to convert'
                      : mergeFiles 
                      ? `${selectedCount} selected files will be combined into a single PDF document`
                      : `Each selected file will be converted to its own separate PDF (${selectedCount} PDFs)`
                    }
                  </p>
                </div>
              )}

              <div className="compression-option">
                <label htmlFor="compression-slider" className="compression-label">
                  PDF Compression Quality: {compressionLevel}%
                </label>
                <div className="compression-slider-container">
                  <span className="compression-min">0% (No compression)</span>
                  <input
                    id="compression-slider"
                    type="range"
                    min="0"
                    max="100"
                    value={compressionLevel}
                    onChange={(e) => setCompressionLevel(parseInt(e.target.value))}
                    className="compression-slider"
                  />
                  <span className="compression-max">100% (Max compression)</span>
                </div>
                <p className="compression-description">
                  Higher values create smaller files but may affect quality. Lower values preserve quality but create larger files.
                </p>
              </div>

              <div className="control-buttons">
                <button
                  className="btn convert-btn"
                  onClick={() => {
                    analytics.trackButtonClick('convert_files', 'pdf_converter');
                    handleConvertAll();
                  }}
                  disabled={isConverting || selectedFiles.filter(file => file.selected).length === 0}
                  style={{
                    background: isConverting || selectedFiles.filter(file => file.selected).length === 0 ? '#6c757d' : '#28a745',
                    minWidth: '200px'
                  }}
                >
                  {(() => {
                    const selectedCount = selectedFiles.filter(file => file.selected).length;
                    return isConverting 
                      ? 'Converting...' 
                      : selectedCount === 0
                        ? 'Select files to convert'
                        : mergeFiles && selectedCount > 1
                          ? `Merge ${selectedCount} files into 1 PDF`
                          : `Convert ${selectedCount} file(s) to PDF`;
                  })()}
                </button>
                
                {failedCount > 0 && (
                  <button
                    className="btn retry-btn"
                    onClick={handleRetryFailed}
                    disabled={isConverting}
                    style={{
                      background: '#ffc107',
                      color: '#000'
                    }}
                  >
                    Retry Failed ({failedCount})
                  </button>
                )}
              </div>

              {(isConverting || isClearing) && (
                <div className="progress-section">
                  <div className="progress-bar">
                    <div 
                      className="progress-fill"
                      style={{ 
                        width: isClearing ? '100%' : `${conversionProgress}%`,
                        backgroundColor: isClearing ? '#6c757d' : '#28a745'
                      }}
                    />
                  </div>
                  <p className="progress-text">
                    {isClearing 
                      ? 'Clearing all data and memory...' 
                      : `${Math.round(conversionProgress)}% - ${currentFile}`
                    }
                  </p>
                </div>
              )}
            </div>
            );
          })()}
        </div>

        {conversionResults.length > 0 && (
          <div className="card results-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0 }}>Conversion Results</h2>
              <button
                className="btn"
                onClick={handleClearResults}
                disabled={isClearing}
                style={{
                  background: isClearing ? '#999' : '#6c757d',
                  fontSize: '14px',
                  padding: '8px 16px'
                }}
                title="Clear conversion results (keep uploaded files for re-conversion)"
              >
                {isClearing ? '🔄 Clearing...' : '🗑️ Clear Results'}
              </button>
            </div>
            
            {conversionResults[0]?.merged ? (
              // Merged PDF result
              <div className="merged-results">
                {conversionResults[0].success ? (
                  <div className="result-item success merged-item">
                    <div className="result-info">
                      <div className="result-name">
                        ✅ Merged PDF Created Successfully
                      </div>
                      <div className="merge-details">
                        📄 {conversionResults[0].processedFiles} of {conversionResults[0].totalFiles} files merged
                        {conversionResults[0].errors?.length > 0 && (
                          <span> • {conversionResults[0].errors.length} files had errors</span>
                        )}
                      </div>
                    </div>
                    <button
                      className="btn download-btn"
                      onClick={() => {
                        const timestamp = generateTimestamp();
                        PDFService.downloadPDF(conversionResults[0].pdf, `merged-pdf-${timestamp}.pdf`);
                      }}
                      style={{
                        background: '#17a2b8',
                        fontSize: '14px',
                        padding: '8px 16px'
                      }}
                    >
                      Download Merged PDF
                    </button>
                  </div>
                ) : (
                  <div className="result-item error merged-item">
                    <div className="result-info">
                      <div className="result-name">❌ Merge Failed</div>
                      <div className="result-error">{conversionResults[0].message}</div>
                    </div>
                  </div>
                )}
                
                {conversionResults[0].errors?.length > 0 && (
                  <div className="error-details">
                    <h3>Files with Errors:</h3>
                    <ul>
                      {conversionResults[0].errors.map((error, index) => (
                        <li key={index} className="error-item">
                          <strong>{getFileName(error.file) || 'Unknown file'}:</strong> {error.error}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              // Individual PDF results
              <>
                <div className="results-summary">
                  <span className="success-count">✅ {successCount} successful</span>
                  {failedCount > 0 && (
                    <span className="failed-count">❌ {failedCount} failed</span>
                  )}
                </div>
                
                <div className="results-list">
                  {conversionResults.map((result, index) => (
                    <div key={index} className={`result-item ${result.success ? 'success' : 'error'}`}>
                      <div className="result-info">
                        <div className="result-name">
                          {result.success ? '✅' : '❌'} {getFileName(result.file) || `File ${index + 1}`}
                        </div>
                        {result.error && (
                          <div className="result-error">{result.error}</div>
                        )}
                      </div>
                      {result.success && (
                        <button
                          className="btn download-btn"
                          onClick={() => handleDownloadSingle(result)}
                          style={{
                            background: '#17a2b8',
                            fontSize: '14px',
                            padding: '8px 16px'
                          }}
                        >
                          Download PDF
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        <div className="pdf-footer">
          <p>
            <strong>Supported formats:</strong> Images (JPG, PNG, GIF, BMP, TIFF, WebP), 
            Text files (TXT), Documents (DOC, DOCX), Spreadsheets (XLS, XLSX), 
            Presentations (PPT, PPTX), Web files (HTML), Markdown (MD)
          </p>
          <p><small>Maximum file size: 10MB per file</small></p>
          <div className="pdf-footer-bottom">
            <p className="pdf-copyright">
              © {new Date().getFullYear()} Utility Tools. All rights reserved.
            </p>
            <p className="pdf-powered-by">
              Powered by nkcodescribbler
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PDFConverter;